import mime from 'mime-types';

import { getFileExtension } from './file';

/**
 * Converts a data URI to Blob.
 *
 * @param {string} dataURI
 * @return {Object} Blob object.
 */
const dataURItoBlob = (dataURI) => {
  // convert base64 to raw binary data held in a string
  const byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component. Example: dataURI = 'data:image/jpeg;base64,<base64_here>' -> result: image/jpeg
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const typedArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    typedArray[i] = byteString.charCodeAt(i);
  }

  const dataView = new DataView(arrayBuffer);

  return new Blob([dataView], { type: mimeString });
};

/**
 * Converts a data URI to a Blob URL.
 * NOTE: Always call URL.revokeObjectURL(blobURL); once it is not longer used. Otherwise a memory leak will happen.
 *
 * @param {string} dataURI
 * @return {string} Blob URL.
 */
const dataURItoBlobURL = (dataURI) => URL.createObjectURL(dataURItoBlob(dataURI));

/**
 * A run of header bytes read as ASCII, for the signatures that are readable tags.
 *
 * @param {Uint8Array} bytes
 * @param {number} start
 * @param {number} end
 * @returns {string}
 */
const asAscii = (bytes, start, end) => String.fromCharCode(...bytes.subarray(start, end));

/**
 * Detects an ISO base media container (HEIC, HEIF, AVIF, MP4, MOV) from its header.
 *
 * Neither the MIME type nor the file extension is trustworthy here. Firebase Storage
 * serves some JPEGs as `image/heic` because the mobile picker reports the *source*
 * asset's MIME type, and Android uploads of a HEIC pick are named `*.heic.jpeg` while
 * holding JPEG bytes. Both signals produce false positives, and feeding a JPEG to
 * heic-to throws.
 *
 * This deliberately does not check the major brand. Enumerating HEIF brands means
 * owning a list that rots as new ones appear; instead this is a cheap gate that
 * everything the browser renders natively fails instantly, and heic-to is left as
 * the authority on whether a container really holds a decodable HEIF still.
 *
 * @param {Uint8Array} bytes - at least the first 8 bytes of the file
 * @returns {boolean}
 */
const isIsoBaseMediaContainer = (bytes) => !!bytes
  && bytes.length >= 8
  && asAscii(bytes, 4, 8) === 'ftyp';

/*
 * Enough of the head to recognise any signature below. WebP needs the most: `RIFF`, a
 * four-byte length, then `WEBP`.
 */
const IMAGE_HEADER_BYTES = 12;

/*
 * Magic numbers for the image types a browser renders natively, each a predicate over the
 * first `IMAGE_HEADER_BYTES`. Only these are listed because the one caller uses the result
 * to *relabel* a file, and a label is only worth writing when the browser can act on it.
 * ISO containers are absent on purpose - `isIsoBaseMediaContainer` owns those, and telling
 * HEIC from AVIF means enumerating brands, which this module avoids everywhere else.
 */
const NATIVE_IMAGE_SIGNATURES = [
  { type: 'image/jpeg', matches: (b) => b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF },
  {
    type: 'image/png',
    matches: (b) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47
      && b[4] === 0x0D && b[5] === 0x0A && b[6] === 0x1A && b[7] === 0x0A,
  },
  { type: 'image/gif', matches: (b) => asAscii(b, 0, 4) === 'GIF8' },
  { type: 'image/webp', matches: (b) => asAscii(b, 0, 4) === 'RIFF' && asAscii(b, 8, 12) === 'WEBP' },
  { type: 'image/bmp', matches: (b) => asAscii(b, 0, 2) === 'BM' },
];

/**
 * The image type a file's first bytes actually are, ignoring what it is named or labelled.
 *
 * The counterpart to `isIsoBaseMediaContainer`: that one answers "might the decoder want
 * this", this one answers "what is it really". Recognises only natively renderable stills,
 * so an empty result means "not something this app can put a truthful label on" - a HEIF
 * still, an unknown format, or bytes that are not an image at all.
 *
 * @param {Uint8Array} bytes - at least the first IMAGE_HEADER_BYTES of the file
 * @returns {string} a MIME type, or '' when nothing matched
 */
const sniffImageType = (bytes) => {
  if (!bytes || bytes.length < IMAGE_HEADER_BYTES) {
    return '';
  }
  return NATIVE_IMAGE_SIGNATURES.find(({ matches }) => matches(bytes))?.type || '';
};

/*
 * Image types every target browser decodes natively. Kept as a deny list rather than an
 * allow list on purpose: an unfamiliar image type falls through to the byte gate in
 * `convertHeicIfNeeded`, which is the real authority. AVIF is listed because it is an ISO
 * container, so without it every AVIF upload would load the decoder only to be rejected.
 */
const NATIVELY_DECODABLE_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml',
  'image/avif',
];

/*
 * What a browser reports when it cannot identify a file. Windows Chrome gives one of
 * these for `.heic`, because the type is not registered with the OS - so the extension
 * has to be believed instead.
 */
const UNINFORMATIVE_MIME_TYPES = ['', 'application/octet-stream', 'binary/octet-stream'];

const CONVERTED_UPLOAD_FORMAT = { fileFormat: 'jpeg', fileType: 'image/jpeg' };

/**
 * Whether an upload is worth handing to the HEIC decoder, judged from its labels alone.
 *
 * This exists to keep videos out. MP4 and MOV are ISO base media containers, so they pass
 * the `ftyp` byte gate and would each load the heic-to wasm bundle only to fail. It also
 * spares the common JPEG/PNG case that same cost.
 *
 * The extension is consulted whenever the reported type is missing or generic, because
 * two callers pass no type at all and some browsers report none for HEIC.
 *
 * @param {{ fileType?: string, fileFormat?: string }} labels
 * @returns {boolean}
 */
const isConvertibleImageUpload = ({ fileType = '', fileFormat = '' } = {}) => {
  const declaredType = String(fileType || '').toLowerCase();
  const typeFromExtension = (fileFormat && mime.lookup(fileFormat)) || '';
  const effectiveType = UNINFORMATIVE_MIME_TYPES.includes(declaredType) ? typeFromExtension : declaredType;

  return effectiveType.startsWith('image/') && !NATIVELY_DECODABLE_IMAGE_TYPES.includes(effectiveType);
};

/**
 * Converts a HEIC/HEIF blob to a JPEG blob. Returns the original blob unchanged for
 * anything else, including containers heic-to cannot decode.
 * @param {Blob} blob
 * @returns {Promise<Blob>}
 */
const convertHeicIfNeeded = async (blob) => {
  const header = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  if (!isIsoBaseMediaContainer(header)) {
    return blob;
  }
  try {
    const { heicTo } = await import('heic-to');
    return await heicTo({ blob, type: 'image/jpeg', quality: 0.85 });
  } catch {
    // An ISO container that is not a decodable HEIF still - AVIF, MP4, or a truncated
    // file. Hand back the original and let the browser do what it can with it.
    return blob;
  }
};

/**
 * Converts a picked File to JPEG when a browser cannot render it, renaming it so the
 * extension matches the bytes.
 *
 * For screens that preview the picked file before uploading it. Converting at upload is
 * too late for those: the preview draws the original into an `<img>`, which stays blank
 * for HEIC. Both halves of the returned File matter - callers draw its bytes and read its
 * name for the stored extension, so renaming is not cosmetic.
 *
 * @param {File} file
 * @returns {Promise<File>} the original file when nothing needed converting
 */
const toDisplayableFile = async (file) => {
  // Same guard as the upload path, for the same reason: MP4 and MOV are ISO containers,
  // so they clear the byte gate inside convertHeicIfNeeded and would pull in the decoder
  // just to fail. Chat previews every non-audio attachment through here, and video is an
  // ordinary attachment there.
  const fileFormat = getFileExtension(file.name);
  if (!isConvertibleImageUpload({ fileType: file.type, fileFormat })) {
    return file;
  }

  const converted = await convertHeicIfNeeded(file);
  if (converted === file) {
    return file;
  }

  const baseName = file.name.replace(/\.[^/.]+$/, '') || 'image';
  return new File(
    [converted],
    `${baseName}.${CONVERTED_UPLOAD_FORMAT.fileFormat}`,
    { type: CONVERTED_UPLOAD_FORMAT.fileType },
  );
};

/**
 * Reads the bytes behind an upload url and returns them with the extension and content
 * type they must be stored under, converting a HEIF still to JPEG on the way.
 *
 * All three move together deliberately: writing bytes under a name and content type that
 * contradict them recreates the exact class of mislabelled object this exists to stop
 * producing. That cuts both ways - converted bytes must lose the `.heic` labels, and bytes
 * that were never HEIC in the first place must lose them too. A `.heic`-named JPEG stamped
 * `image/heic` is the commonest case in production, and storing it unchanged leaves Chrome
 * refusing an image it could have rendered.
 *
 * Callers that pass no type also get one derived from the extension, which replaces a
 * bare `heic` - not a valid content type - with `image/heic`.
 *
 * @param {{ url: string, fileFormat?: string, fileType?: string }} upload
 * @returns {Promise<{ blob: Blob, fileFormat: string, fileType: string }>}
 */
const prepareUploadBlob = async ({ url, fileFormat = '', fileType = '' }) => {
  const blob = await fetch(url).then((response) => response.blob());
  const resolvedType = fileType || mime.lookup(fileFormat) || '';
  const unconverted = { blob, fileFormat, fileType: resolvedType };

  // A blob read from an object url carries the original File's type, which is a free
  // second opinion when the caller supplied none.
  if (!isConvertibleImageUpload({ fileType: fileType || blob.type, fileFormat })) {
    return unconverted;
  }

  const converted = await convertHeicIfNeeded(blob);
  if (converted !== blob) {
    return { blob: converted, ...CONVERTED_UPLOAD_FORMAT };
  }

  // The same blob comes back for two different reasons and only the bytes tell them apart.
  // A failed decode leaves the original labels as the best description there is; a blob the
  // byte gate rejected has just been proven not to be what its labels claim, so it is
  // relabelled to match. Sniffing answers both at once - a HEIF still matches nothing here.
  const header = new Uint8Array(await blob.slice(0, IMAGE_HEADER_BYTES).arrayBuffer());
  const sniffedType = sniffImageType(header);

  return sniffedType
    ? { blob, fileFormat: mime.extension(sniffedType), fileType: sniffedType }
    : unconverted;
};

/**
 * Fetches a URL and converts it to a JPEG blob URL if it's a HEIC/HEIF image.
 * Returns the original URL unchanged for non-HEIC images.
 * When isBlob is true, the caller is responsible for calling URL.revokeObjectURL(safeUrl) when done.
 * @param {string} url - the URL to fetch
 * @returns {Promise<{ safeUrl: string, isBlob: boolean }>}
 */
const fetchAsSafeImageUrl = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const safeBlob = await convertHeicIfNeeded(blob);
  if (safeBlob !== blob) {
    return { safeUrl: URL.createObjectURL(safeBlob), isBlob: true };
  }
  return { safeUrl: url, isBlob: false };
};

/**
 * A remote image as an object URL, HEIC converted first.
 *
 * Unlike fetchAsSafeImageUrl this always returns an object URL, which matters when the result is
 * handed to a canvas: cropper.js reads the image into one, and `toDataURL()` throws a SecurityError on
 * a canvas tainted by a cross-origin source. So use this rather than the address itself whenever the
 * image will be drawn into a canvas. Callers own the URL and must revoke it.
 * @param {string} url
 * @returns {Promise<string>} Object URL.
 */
const fetchAsBlobUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load the image (${response.status})`);
  }
  const blob = await convertHeicIfNeeded(await response.blob(), url);
  return URL.createObjectURL(blob);
};

/**
 * Downloads a file from firebase storage and returns its blob, HEIC converted to JPEG first.
 *
 * Prefer this over storageURLtoBlobURL whenever the caller wants the bytes rather than an address -
 * saving a file to disk, for instance, where the blob's type is the only trustworthy answer to what
 * the bytes actually are, and where there is no object URL to remember to revoke.
 *
 * @param storagePath
 * @param storage firebase storage instance
 * @returns {Promise<Blob>} File blob.
 */
const storageURLtoBlob = async (storagePath, storage) => {
  const downloadUrl = await storage
    .ref()
    .child(storagePath)
    .getDownloadURL();
  const fileData = await fetch(downloadUrl);
  return convertHeicIfNeeded(await fileData.blob());
};

/**
 * Downloads a file from firebase storage and returns a blob URL
 * NOTE: Always call URL.revokeObjectURL(blobURL); once it is no longer used. Otherwise a memory leak
 * will happen.
 * @param storagePath
 * @param storage firebase storage instance
 * @returns {Promise<string>} Blob URL.
 */
const storageURLtoBlobURL = async (storagePath, storage) => (
  URL.createObjectURL(await storageURLtoBlob(storagePath, storage))
);

/**
 * Downloads a JSON file from firebase storage and returns the JSON content
 * @param storagePath
 * @param storage firebase storage instance
 * @returns {Promise<Object>} JSON object.
 */
const storageJSONURLtoJSONData = async (storagePath, storage) => {
  const downloadUrl = await storage
    .ref()
    .child(`${storagePath}.json`)
    .getDownloadURL();
  const fileData = await fetch(downloadUrl);
  const jsonData = await fileData.json();
  return jsonData;
};

/**
 * Converts a given File object to an ArrayBuffer.
 *
 * @param {File} file - The file object to be converted.
 * @returns {Promise<ArrayBuffer>} A promise that resolves tothe ArrayBuffer of the provided file.
 * @throws an error if reading the file fails.
 */
const fileToArrayBuffer = async (file) => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  })
);

/**
 * Converts an array of objects into a keyed JSON object.
 *
 * @param {Array} arrOfObjects - The array to be converted.
 * @param {string} objKey - The key to be used from each object as the key for the created key value pairs.
 * @returns {Object} A keyed JSON object
 * @example
 * const array = [{keyAttr: key1, valueAttr: value1}, {keyAttr: key2, valueAttr: value2}, ...];
 * const converted = arrOfObjectstoKeyedJSONObj(array, 'keyAttr');
 * converted -> {key1: {keyAttr: key1, valueAttr: value1}, key2: {keyAttr: key2, valueAttr: value2}, ...};
 */

const arrOfObjectstoKeyedJSONObj = (arrOfObjects, objKey) => (
  arrOfObjects.reduce((accumulator, obj) => {
    const key = obj[objKey];
    accumulator[key] = obj;
    return accumulator;
  }, {})
);

export {
  dataURItoBlob,
  dataURItoBlobURL,
  isIsoBaseMediaContainer,
  sniffImageType,
  isConvertibleImageUpload,
  CONVERTED_UPLOAD_FORMAT,
  convertHeicIfNeeded,
  toDisplayableFile,
  prepareUploadBlob,
  fetchAsSafeImageUrl,
  fetchAsBlobUrl,
  storageURLtoBlob,
  storageURLtoBlobURL,
  storageJSONURLtoJSONData,
  fileToArrayBuffer,
  arrOfObjectstoKeyedJSONObj,
};
